<template>
  <v-container class="d-flex flex-column align-start" pa-0 fluid fill-height>
    <FullHeader />
    <div class="planning-container">

      <!-- Config bar -->
      <div class="config-bar">
        <span class="config-label mr-2">Inicio</span>
        <v-text-field
          v-model="config.startDate"
          type="date"
          :disabled="configLocked"
          solo flat dense hide-details
          class="config-field mr-2"
        />
        <span class="config-label mr-2">Fin</span>
        <v-text-field
          v-model="config.endDate"
          type="date"
          :disabled="configLocked"
          solo flat dense hide-details
          class="config-field mr-2"
        />
        <v-select
          v-model="config.cutoffDay"
          :items="weekdays"
          item-text="label"
          item-value="value"
          placeholder="Día corte"
          :disabled="configLocked"
          solo flat dense hide-details
          class="config-field-sm mr-2"
        />
        <v-btn
          v-if="!configLocked"
          color="primary"
          depressed small
          :disabled="!canGenerate || !hasPermission('create_planning')"
          @click="generatePeriods"
          class="generate-btn mr-2"
        >
          <v-icon left small>mdi-calendar-check</v-icon>
          Generar
        </v-btn>
        <v-btn v-else text small color="grey darken-1" @click="unlockConfig" :disabled="!hasPermission('create_planning')" class="mr-2">
          <v-icon left small>mdi-pencil-outline</v-icon>
          Editar
        </v-btn>

        <v-btn
          outlined small
          color="green darken-1"
          @click="triggerFileInput"
          class="mr-1"
          :loading="importingExcel"
          :disabled="!hasPermission('create_planning')"
        >
          <v-icon left small>mdi-microsoft-excel</v-icon>
          Importar Excel
        </v-btn>

        <input
          ref="excelFileInput"
          type="file"
          accept=".xlsx,.xls"
          style="display:none"
          @change="onExcelFileSelected"
        />

        <v-btn
          text small
          color="grey darken-1"
          href="https://laot-dev.s3.us-east-2.amazonaws.com/commons/plantilla_planificacion.xlsx"
          target="_blank"
          download="plantilla_planificacion.xlsx"
        >
          <v-icon left small>mdi-download-outline</v-icon>
          Plantilla
        </v-btn>
        
      </div>

      <!-- Import error alert -->
      <v-alert
        v-if="importError"
        type="error"
        dense
        dismissible
        class="mx-4 mt-2"
        style="flex-shrink:0; font-size:12px;"
        @input="importError = ''"
      >
        {{ importError }}
      </v-alert>

      <!-- Resizable split -->
      <div v-if="rows.length" class="content-split" ref="splitContainer">

        <!-- Table pane -->
        <div class="table-pane" :style="{ width: tablePct + '%' }">
          <div class="pane-inner">
            <div class="table-header">
              <span class="section-title">Períodos ({{ rows.length }})</span>
              <div class="d-flex align-center" style="gap:6px;">
                <div class="d-flex align-center" style="gap:2px; border-right:1px solid #E5E7EB; padding-right:6px;">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon x-small v-on="on" v-bind="attrs" @click="showEc = !showEc" :color="showEc ? '#2D4B95' : '#D1D5DB'">
                        <v-icon x-small>mdi-currency-usd</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ showEc ? 'Ocultar' : 'Mostrar' }} Económico</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon x-small v-on="on" v-bind="attrs" @click="showPh = !showPh" :color="showPh ? '#0EA5E9' : '#D1D5DB'">
                        <v-icon x-small>mdi-ruler</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ showPh ? 'Ocultar' : 'Mostrar' }} Físico</span>
                  </v-tooltip>
                </div>
                <v-btn outlined x-small color="amber darken-2" @click="addReprogramLabel" :disabled="!hasPermission('create_planning')">
                  <v-icon left x-small>mdi-plus</v-icon>Reprogramación
                </v-btn>
                <v-btn depressed x-small color="primary" @click="addRow" :disabled="!hasPermission('create_planning')">
                  <v-icon left x-small>mdi-plus</v-icon>Período
                </v-btn>
              </div>
            </div>

            <div class="table-wrapper">
              <table>
                <thead>
                  <tr class="col-group-row">
                    <th rowspan="2" class="th-left px-3">Fecha</th>
                    <th v-if="visibleColspan > 0" :colspan="visibleColspan" class="grp-header grp-plan">Plan</th>
                    <th v-if="visibleColspan > 0" :colspan="visibleColspan" class="grp-header grp-actual">Real</th>
                    <th
                      v-for="(label, idx) in reprogramLabels"
                      :key="'grp-' + idx"
                      :colspan="visibleColspan"
                      class="grp-header"
                      :style="{ color: reprogColors[idx % reprogColors.length], borderBottomColor: reprogColors[idx % reprogColors.length] }"
                    >
                      <div class="reprog-grp-label">
                        {{ label }}
                        <v-btn icon x-small @click="removeReprogramLabel(idx)" :disabled="!hasPermission('create_planning')" color="grey lighten-1" style="margin-left:2px">
                          <v-icon x-small>mdi-close</v-icon>
                        </v-btn>
                      </div>
                    </th>
                    <th rowspan="2"></th>
                  </tr>
                  <tr class="col-sub-row">
                    <template v-if="showEc">
                      <th class="sub-th ec-th">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon x-small color="#2D4B95" v-on="on" v-bind="attrs">mdi-currency-usd</v-icon>
                          </template>
                          <span>Avance Económico</span>
                        </v-tooltip>
                        <div v-if="contractValue" class="sub-th-ref">val. ref.</div>
                      </th>
                    </template>
                    <th v-if="showPh" class="sub-th ph-th">
                      <v-tooltip bottom><template v-slot:activator="{ on, attrs }"><v-icon x-small color="#0EA5E9" v-on="on" v-bind="attrs">mdi-ruler</v-icon></template><span>Avance Físico</span></v-tooltip>
                    </th>
                    <th v-if="showEc" class="sub-th ec-th">
                      <v-tooltip bottom><template v-slot:activator="{ on, attrs }"><v-icon x-small color="#16A34A" v-on="on" v-bind="attrs">mdi-currency-usd</v-icon></template><span>Avance Económico Real</span></v-tooltip>
                    </th>
                    <th v-if="showPh" class="sub-th ph-th">
                      <v-tooltip bottom><template v-slot:activator="{ on, attrs }"><v-icon x-small color="#22C55E" v-on="on" v-bind="attrs">mdi-ruler</v-icon></template><span>Avance Físico Real</span></v-tooltip>
                    </th>
                    <template v-for="(label, idx) in reprogramLabels">
                      <th v-if="showEc" :key="'sub-ec-' + idx" class="sub-th ec-th">
                        <v-tooltip bottom><template v-slot:activator="{ on, attrs }"><v-icon x-small :color="reprogColors[idx % reprogColors.length]" v-on="on" v-bind="attrs">mdi-currency-usd</v-icon></template><span>Avance Económico</span></v-tooltip>
                      </th>
                      <th v-if="showPh" :key="'sub-ph-' + idx" class="sub-th ph-th">
                        <v-tooltip bottom><template v-slot:activator="{ on, attrs }"><v-icon x-small :color="reprogColors[idx % reprogColors.length]" v-on="on" v-bind="attrs">mdi-ruler</v-icon></template><span>Avance Físico</span></v-tooltip>
                      </th>
                    </template>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, rowIndex) in rows" :key="row._id">
                    <td style="width: 120px; max-width: 120px; min-width: 120px;">
                      <div class="date-wrap">
                        <input type="date" v-model="row.date" class="date-input" :disabled="!hasPermission('create_planning')" />
                      </div>
                    </td>

                    <td v-if="showEc">
                      <div class="pct-wrap ec-wrap">
                        <input
                          type="number" v-model.number="row.ec_programmed_progress"
                          min="0" max="100" step="0.1"
                          class="pct-input" placeholder="—"
                          :tabindex="tabIdx(rowIndex, 0)"
                          :disabled="!hasPermission('create_planning')"
                        />
                        <span class="pct-sym">%</span>
                      </div>
                      <div v-if="contractValue && row.ec_programmed_progress !== null && row.ec_programmed_progress !== ''" class="ref-value">
                        {{ formatCurrency(contractValue * row.ec_programmed_progress / 100) }}
                      </div>
                    </td>

                    <td v-if="showPh">
                      <div class="pct-wrap ph-wrap">
                        <input
                          type="number" v-model.number="row.ph_programmed_progress"
                          min="0" max="100" step="0.1"
                          class="pct-input" placeholder="—"
                          :tabindex="tabIdx(rowIndex, 1)"
                          :disabled="!hasPermission('create_planning')"
                        />
                        <span class="pct-sym">%</span>
                      </div>
                    </td>

                    <td v-if="showEc">
                      <div :class="['pct-wrap','ec-wrap', { 'is-disabled': isDateAfterToday(row.date) }]">
                        <input
                          type="number" v-model.number="row.ec_actual_progress"
                          min="0" max="100" step="0.1"
                          class="pct-input" placeholder="—"
                          :tabindex="tabIdx(rowIndex, 2)"
                          :disabled="isDateAfterToday(row.date) || !hasPermission('update_planning_progress')"
                        />
                        <span class="pct-sym">%</span>
                      </div>
                      <div v-if="contractValue && row.ec_actual_progress !== null && row.ec_actual_progress !== ''" class="ref-value ref-value--actual">
                        {{ formatCurrency(contractValue * row.ec_actual_progress / 100) }}
                      </div>
                    </td>

                    <td v-if="showPh">
                      <div :class="['pct-wrap','ph-wrap', { 'is-disabled': isDateAfterToday(row.date) }]">
                        <input
                          type="number" v-model.number="row.ph_actual_progress"
                          min="0" max="100" step="0.1"
                          class="pct-input" placeholder="—"
                          :tabindex="tabIdx(rowIndex, 3)"
                          :disabled="isDateAfterToday(row.date) || !hasPermission('update_planning_progress')"
                        />
                        <span class="pct-sym">%</span>
                      </div>
                    </td>

                    <template v-for="(label, idx) in reprogramLabels">
                      <td v-if="showEc" :key="'rep-ec-' + idx">
                        <div class="pct-wrap ec-wrap">
                          <input
                            type="number"
                            :value="row.reprogram[idx] ? row.reprogram[idx].ec_progress : null"
                            @input="updateReprog(row, idx, 'ec_progress', $event)"
                            min="0" max="100" step="0.1"
                            class="pct-input" placeholder="—"
                            :tabindex="tabIdx(rowIndex, 4 + idx * 2)"
                            :disabled="!hasPermission('create_planning')"
                          />
                          <span class="pct-sym">%</span>
                        </div>
                      </td>
                      <td v-if="showPh" :key="'rep-ph-' + idx">
                        <div class="pct-wrap ph-wrap">
                          <input
                            type="number"
                            :value="row.reprogram[idx] ? row.reprogram[idx].ph_progress : null"
                            @input="updateReprog(row, idx, 'ph_progress', $event)"
                            min="0" max="100" step="0.1"
                            class="pct-input" placeholder="—"
                            :tabindex="tabIdx(rowIndex, 4 + idx * 2 + 1)"
                            :disabled="!hasPermission('create_planning')"
                          />
                          <span class="pct-sym">%</span>
                        </div>
                      </td>
                    </template>

                    <td class="text-center">
                      <v-btn
                        icon x-small
                        :disabled="rows.length === 1"
                        @click="removeRow(rowIndex)"
                        color="grey lighten-1"
                      >
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Drag handle -->
        <div
          class="drag-handle"
          :class="{ 'drag-handle--active': dragging }"
          @mousedown="startDrag"
        >
          <div class="drag-grip">
            <span /><span /><span /><span /><span />
          </div>
        </div>

        <!-- Chart pane -->
        <div class="chart-pane" :style="{ width: (100 - tablePct) + '%' }">
          <div class="pane-inner">

            <!-- ── KPI Accordion ── -->
            <div class="kpi-accordion" :class="{ 'kpi-accordion--open': kpiOpen }">

              <!-- Collapsed bar (always visible) -->
              <div class="kpi-bar" @click="kpiOpen = !kpiOpen">
                <!-- Scrollable KPI items (left side) -->
                <div class="kpi-bar-items">
                  <span class="kpi-status-badge" :class="statusClass">
                    <span class="kpi-status-dot" />
                    {{ statusLabel }}
                  </span>

                  <span class="kpi-bar-sep" />

                  <span class="kpi-bar-item">
                    <span class="kpi-bar-label">Tiempo</span>
                    <span class="kpi-bar-val">{{ timePct !== null ? timePct + '%' : '—' }}</span>
                  </span>

                  <span class="kpi-bar-sep" />

                  <span class="kpi-bar-item" v-if="lastEcPlanEffective !== null || lastPhPlanEffective !== null">
                    <span class="kpi-bar-label">Planificación</span>
                    <span class="kpi-bar-val">
                      <span v-if="lastEcPlanEffective !== null">Ec. {{ lastEcPlanEffective }}%</span>
                      <span v-if="lastPhPlanEffective !== null" style="margin-left:6px;">Fís. {{ lastPhPlanEffective }}%</span>
                    </span>
                  </span>

                  <span class="kpi-bar-item" v-if="lastActualEc !== null">
                    <span class="kpi-bar-label">Real Ec.</span>
                    <span class="kpi-bar-val">{{ lastActualEc }}%</span>
                  </span>

                  <span class="kpi-bar-sep" v-if="lastActualEc !== null" />

                  <span class="kpi-bar-item" v-if="spi !== null">
                    <span class="kpi-bar-label">SPI</span>
                    <span class="kpi-bar-val" :class="spiClass">{{ spi }}</span>
                  </span>
                </div>

                <!-- Toggle button always pinned right -->
                <span class="kpi-toggle-btn">
                  <span class="kpi-toggle-label">{{ kpiOpen ? 'Ocultar' : 'Ver detalle' }}</span>
                  <v-icon x-small color="#6366F1" style="transition: transform 0.25s ease" :style="{ transform: kpiOpen ? 'rotate(180deg)' : 'rotate(0deg)' }">
                    mdi-chevron-down
                  </v-icon>
                </span>
              </div>

              <!-- Expanded grid -->
              <div class="kpi-grid" v-show="kpiOpen">

                <!-- Contrato -->
                <!-- <div class="kpi-card" v-if="contractValue">
                  <div class="kpi-card-icon" style="background:#EFF6FF">
                    <v-icon x-small color="#2D4B95">mdi-file-sign</v-icon>
                  </div>
                  <div class="kpi-card-body">
                    <div class="kpi-card-label">Contrato</div>
                    <div class="kpi-card-value">{{ formatCurrency(contractValue) }}</div>
                    <div class="kpi-card-sub" v-if="selectedProject && selectedProject.details">{{ selectedProject.details.total_days }} días</div>
                  </div>
                </div> -->

                <!-- Tiempo transcurrido -->
                <!-- <div class="kpi-card" v-if="timePct !== null">
                  <div class="kpi-card-icon" style="background:#F0FDF4">
                    <v-icon x-small color="#16A34A">mdi-timer-outline</v-icon>
                  </div>
                  <div class="kpi-card-body">
                    <div style="display:flex; align-items:center; gap:6px">
                      <div class="kpi-card-label">Tiempo transcurrido</div>
                      <v-menu bottom open-on-hover offset-y content-class="entrega-approval-menu">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon x-small color="#16A34A" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                        </template>
                        <div class="kpi-tooltip">
                          <div class="kpi-tooltip-title">Tiempo transcurrido</div>
                          <div class="kpi-tooltip-body">Es cuánto del plazo total del contrato ya pasó, contando los días desde el inicio hasta hoy.</div>
                        </div>
                      </v-menu>
                    </div>
                    <div class="kpi-card-value">{{ timePct }}%</div>
                    <div class="kpi-card-sub">
                      Término: {{ contractEndDateFormatted || '—' }}
                    </div>
                  </div>
                </div> -->

                <!-- Planificación (mayor entre plan original y reprogramaciones) -->
                <div class="kpi-card" v-if="lastEcPlanEffective !== null || lastPhPlanEffective !== null">
                  <div class="kpi-card-icon" style="background:#EFF6FF">
                    <v-icon x-small color="#2D4B95">mdi-currency-usd</v-icon>
                  </div>
                  <div class="kpi-card-body">
                    <div style="display:flex; align-items:center; gap:6px">
                      <div class="kpi-card-label">Planificación</div>
                      <v-menu bottom open-on-hover offset-y content-class="entrega-approval-menu">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon x-small color="#2D4B95" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                        </template>
                        <div class="kpi-tooltip">
                          <div class="kpi-tooltip-title">Planificación</div>
                          <div class="kpi-tooltip-body">Es el avance que el proyecto debería tener según lo programado. Si se hicieron reprogramaciones, se toma como referencia la más reciente.</div>
                        </div>
                      </v-menu>
                    </div>
                    <div class="kpi-card-value" style="color:#2D4B95">
                      <span v-if="lastEcPlanEffective !== null">Ec. {{ lastEcPlanEffective }}%</span>
                    </div>
                    <div class="kpi-card-sub" v-if="lastPhPlanEffective !== null">Fís. {{ lastPhPlanEffective }}%</div>
                  </div>
                </div>

                <!-- Avance Real (mostrar Económico y Físico) -->
                <div class="kpi-card" v-if="lastActualEc !== null || lastActualPh !== null">
                  <div class="kpi-card-icon" style="background:#F0FDF4">
                    <v-icon x-small color="#16A34A">mdi-check-circle-outline</v-icon>
                  </div>
                  <div class="kpi-card-body">
                    <div style="display:flex; align-items:center; gap:6px">
                      <div class="kpi-card-label">Avance Real</div>
                      <v-menu bottom open-on-hover offset-y content-class="entrega-approval-menu">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon x-small color="#16A34A" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                        </template>
                        <div class="kpi-tooltip">
                          <div class="kpi-tooltip-title">Avance Real</div>
                          <div class="kpi-tooltip-body">Es el avance que realmente se ha logrado hasta la fecha, tanto en términos económicos (dinero) como físicos (obra ejecutada).</div>
                        </div>
                      </v-menu>
                    </div>
                    <div class="kpi-card-value" style="color:#16A34A">
                      <span v-if="lastActualEc !== null">Ec. {{ lastActualEc }}%</span>
                    </div>
                    <div class="kpi-card-sub" v-if="lastActualPh !== null" style="color:#16A34A">Fís. {{ lastActualPh }}%</div>
                  </div>
                </div>

                <!-- Adelanto/Retraso -->
                <div class="kpi-card" v-if="adelantoRetrasoEc !== null || adelantoRetrasoPh !== null">
                  <div class="kpi-card-icon" :style="{ background: desvBg }">
                    <v-icon x-small :color="desvIconColor">mdi-delta</v-icon>
                  </div>
                  <div class="kpi-card-body">
                    <div style="display:flex; align-items:center; gap:6px">
                      <div class="kpi-card-label">Adelanto/Retraso</div>
                      <v-menu bottom open-on-hover offset-y content-class="entrega-approval-menu">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon x-small :color="desvIconColor" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                        </template>
                        <div class="kpi-tooltip">
                          <div class="kpi-tooltip-title">Adelanto / Retraso</div>
                          <div class="kpi-tooltip-body">Muestra si el proyecto va adelantado o atrasado respecto a lo planificado. Un valor positivo significa que va adelantado; uno negativo, que va atrasado.</div>
                        </div>
                      </v-menu>
                    </div>
                    <div class="kpi-card-value" v-if="adelantoRetrasoEc !== null" :class="parseFloat(adelantoRetrasoEc) >= 0 ? 'desv-pos' : 'desv-neg'">
                      Ec. {{ parseFloat(adelantoRetrasoEc) >= 0 ? '+' : '' }}{{ adelantoRetrasoEc }}%
                    </div>
                    <div class="kpi-card-sub" v-if="adelantoRetrasoPh !== null" :class="parseFloat(adelantoRetrasoPh) >= 0 ? 'desv-pos' : 'desv-neg'">
                      Fís. {{ parseFloat(adelantoRetrasoPh) >= 0 ? '+' : '' }}{{ adelantoRetrasoPh }}%
                    </div>
                  </div>
                </div>

                <!-- SPI -->
                <div class="kpi-card" v-if="spi !== null">
                  <div class="kpi-card-icon" :style="{ background: spiBg }">
                    <v-icon x-small :color="spiColor">mdi-speedometer</v-icon>
                  </div>
                  <div class="kpi-card-body">
                    <div style="display:flex; align-items:center; gap:6px">
                      <div class="kpi-card-label">SPI</div>
                      <v-menu bottom open-on-hover offset-y content-class="entrega-approval-menu">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon x-small :color="spiColor" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                        </template>
                        <div class="kpi-tooltip">
                          <div class="kpi-tooltip-title">SPI (Índice de cumplimiento)</div>
                          <div class="kpi-tooltip-body">Compara lo que realmente se ha avanzado con lo que se había planificado. Un valor de 1 o más indica que el proyecto va al día o adelantado; por debajo de 1, indica atraso.</div>
                        </div>
                      </v-menu>
                    </div>
                    <div class="kpi-card-value" :class="spiClass">{{ spi }}</div>
                    <div class="kpi-card-sub" :class="statusClass + '--text'">{{ statusLabel }}</div>
                  </div>
                </div>

                <!-- Proyección de cierre -->
                <div class="kpi-card" v-if="projectedEndDate">
                  <div class="kpi-card-icon" :style="{ background: projectedDaysOver > 0 ? '#FFF1F2' : '#F0FDF4' }">
                    <v-icon x-small :color="projectedDaysOver > 0 ? '#DC2626' : '#16A34A'">mdi-flag-checkered</v-icon>
                  </div>
                  <div class="kpi-card-body">
                    <div class="kpi-card-label">Proyección cierre</div>
                    <div class="kpi-card-value" :class="projectedDaysOver > 0 ? 'desv-neg' : 'desv-pos'">
                      {{ projectedEndDate }}
                    </div>
                    <div class="kpi-card-sub" v-if="projectedDaysOver !== null">
                      <span :class="projectedDaysOver > 0 ? 'desv-neg' : 'desv-pos'">
                        {{ projectedDaysOver > 0 ? '+' : '' }}{{ projectedDaysOver }} días vs contrato
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Velocidad de avance -->
                <div class="kpi-card" v-if="advanceSpeed !== null">
                  <div class="kpi-card-icon" style="background:#F5F3FF">
                    <v-icon x-small color="#7C3AED">mdi-lightning-bolt</v-icon>
                  </div>
                  <div class="kpi-card-body">
                    <div style="display:flex; align-items:center; gap:6px">
                      <div class="kpi-card-label">Velocidad (últ. 4 per.)</div>
                      <v-menu bottom open-on-hover offset-y content-class="entrega-approval-menu">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon x-small color="#7C3AED" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                        </template>
                        <div class="kpi-tooltip">
                          <div class="kpi-tooltip-title">Velocidad de avance</div>
                          <div class="kpi-tooltip-body">Muestra qué tan rápido está avanzando el proyecto en promedio cada semana, según los últimos períodos con datos reales. Se usa para estimar la fecha de término.</div>
                        </div>
                      </v-menu>
                    </div>
                    <div class="kpi-card-value" style="color:#7C3AED">{{ advanceSpeed }}% / sem.</div>
                    <div class="kpi-card-sub" v-if="requiredSpeed !== null">
                      Necesario: {{ requiredSpeed }}% / sem.
                    </div>
                  </div>
                </div>

              </div>
            </div>
            <!-- ── /KPI Accordion ── -->

            <!-- Compact notice bar -->
            <div v-if="!showAllPoints" class="widget-notice">
              <div class="notice-left">
                <v-icon x-small color="#D97706" style="margin-right: 4px;">mdi-information-outline</v-icon>
                <span>Datos resumidos por espacio. Amplía el panel o</span>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <button
                      class="notice-btn"
                      v-on="on" v-bind="attrs"
                      @click="showAllPoints = true"
                    >
                      <v-icon x-small style="margin-right: 2px;">mdi-chart-scatter-plot</v-icon>
                      ver todos los puntos
                    </button>
                  </template>
                  <span>Mostrar todos los puntos del gráfico</span>
                </v-tooltip>
              </div>
            </div>

            <!-- Active notice when showAllPoints is on -->
            <div v-else class="widget-notice widget-notice--active">
              <div class="notice-left">
                <v-icon x-small color="#16A34A" style="margin-right: 4px;">mdi-check-circle-outline</v-icon>
                <span>Mostrando todos los puntos</span>
                <button class="notice-btn notice-btn--green" @click="showAllPoints = false">
                  <v-icon x-small style="margin-right: 2px;">mdi-arrow-collapse-horizontal</v-icon>
                  reducir
                </button>
              </div>
            </div>

            <PlanningLineChart
              :rows="rows"
              :reprogramLabels="reprogramLabels"
              :reprogColors="reprogColors"
              :show-all-points="showAllPoints"
              :effective-plan-ec="lastEcPlanEffective"
              :effective-plan-ph="lastPhPlanEffective"
            />
          </div>
        </div>
      </div>

      <!-- Loading state -->
      <div class="empty-state" v-else-if="loading">
        <v-progress-circular indeterminate color="primary" size="48" />
        <p class="grey--text text--lighten-1 text-center mt-3" style="font-size: 13px;">
          Cargando programación ...
        </p>
      </div>

      <!-- Empty state -->
      <div class="empty-state" v-else>
        <v-icon size="48" color="grey lighten-1">mdi-calendar-blank-outline</v-icon>
        <p class="grey--text text--lighten-1 text-center mt-2" style="max-width: 460px;">
          Configura las fechas y el día de corte para generar los períodos de avance automáticamente,
          o importa un Excel con las columnas: Fecha, % Económico Planificado, % Físico Planificado.
        </p>
      </div>

    </div>
  </v-container>
</template>

<script>
import FullHeader from '../../Globals/Headers/FullHeader'
import PlanningLineChart from '@/components/Charts/PlanningLineChart'
import { setGeneralProjectPlanning, getGeneralProjectPlanning } from '@/helpers/api/planning'
import _debounce from 'lodash/debounce'
import * as XLSX from 'xlsx'
import { checkPermissions } from '@/helpers/permissions'

const REPROG_COLORS = ['#D97706', '#EA4C4C', '#8B5CF6', '#06B6D4', '#F59E0B']

export default {
  name: 'SimplePlanning',
  components: { FullHeader, PlanningLineChart },

  data() {
    return {
      loading: true,
      config: { startDate: '', endDate: '', cutoffDay: null },
      configLocked: false,
      reprogramLabels: [],
      rows: [],
      debouncedSave: () => {},
      showEc: true,
      showPh: false,
      reprogColors: REPROG_COLORS,

      // KPI accordion
      kpiOpen: false,
      showAllPoints: false,
      // showChartOverlay removed: overlay is handled by PlanningLineChart

      // Split resize state
      tablePct: 55,
      dragging: false,
      dragStartX: 0,
      dragStartPct: 55,

      // Excel import
      importingExcel: false,
      importError: '',

      weekdays: [
        { label: 'Lunes',     value: 1 },
        { label: 'Martes',    value: 2 },
        { label: 'Miércoles', value: 3 },
        { label: 'Jueves',    value: 4 },
        { label: 'Viernes',   value: 5 },
        { label: 'Sábado',    value: 6 },
        { label: 'Domingo',   value: 0 },
      ],
    }
  },

  created() {
    this.debouncedSave = _debounce(this.saveTableData, 800)
  },

  beforeDestroy() {
    this.debouncedSave.cancel()
  },

  watch: {
    rows: {
      handler() { this.debouncedSave() },
      deep: true,
    },
    selectedProject(newProj, oldProj) {
      if (newProj && newProj.id !== oldProj?.id) {
        this.rows = []
        this.reprogramLabels = []
        this.configLocked = false
        this.getProjectPlanning()
      }
    },
  },

  computed: {
    selectedProject() {
      return this.$store.projectsStore.state.selectedProject
    },

    contractValue() {
      return this.selectedProject?.details?.contract_value ?? null
    },

    // ── Time & dates ──────────────────────────────────────────────────────

    contractStartDate() {
      const sd = this.selectedProject?.details?.start_date
      if (!sd) return null
      return new Date(sd + 'T00:00:00')
    },

    contractEndDate() {
      const start = this.contractStartDate
      const days  = this.selectedProject?.details?.total_days
      if (!start || !days) return null
      const end = new Date(start)
      end.setDate(end.getDate() + parseInt(days))
      return end
    },

    contractEndDateFormatted() {
      const d = this.contractEndDate
      if (!d) return null
      return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' })
    },

    timePct() {
      const start = this.contractStartDate
      const end   = this.contractEndDate
      if (!start || !end) return null
      const now     = new Date()
      const total   = end - start
      const elapsed = now - start
      if (total <= 0) return null
      const pct = (elapsed / total) * 100
      return Math.min(100, Math.max(0, parseFloat(pct.toFixed(1))))
    },

    // ── SPI ───────────────────────────────────────────────────────────────

    spi() {
      const real = this.lastActualEc !== null ? parseFloat(this.lastActualEc) : null
      const plan = this.lastEcPlan   !== null ? parseFloat(this.lastEcPlan)   : null
      if (real === null || plan === null || plan === 0) return null
      return parseFloat((real / plan).toFixed(2))
    },

    spiClass() {
      if (this.spi === null) return ''
      if (this.spi >= 1)    return 'desv-pos'
      if (this.spi >= 0.85) return 'desv-warn'
      return 'desv-neg'
    },

    spiColor() {
      if (this.spi === null) return '#6B7280'
      if (this.spi >= 1)    return '#16A34A'
      if (this.spi >= 0.85) return '#D97706'
      return '#DC2626'
    },

    spiBg() {
      if (this.spi === null) return '#F9FAFB'
      if (this.spi >= 1)    return '#F0FDF4'
      if (this.spi >= 0.85) return '#FFFBEB'
      return '#FFF1F2'
    },

    statusLabel() {
      if (this.spi === null) return 'Sin datos'
      if (this.spi >= 1)    return 'En plazo'
      if (this.spi >= 0.85) return 'En riesgo'
      return 'Atrasado'
    },

    statusClass() {
      if (this.spi === null) return 'status--neutral'
      if (this.spi >= 1)    return 'status--ok'
      if (this.spi >= 0.85) return 'status--warn'
      return 'status--danger'
    },

    // ── Projected end date ────────────────────────────────────────────────

    advanceSpeed() {
      // Average % advance per week over last 4 periods with real data (only up to today)
      const withReal = this._rowsUpTo().filter(r => r.ec_actual_progress !== null && r.ec_actual_progress !== '')
      if (withReal.length < 2) return null
      const last4 = withReal.slice(-4)
      const first  = parseFloat(last4[0].ec_actual_progress)
      const latest = parseFloat(last4[last4.length - 1].ec_actual_progress)
      const weeks  = last4.length - 1
      if (weeks === 0) return null
      return parseFloat(((latest - first) / weeks).toFixed(2))
    },

    projectedEndDate() {
      const speed = this.advanceSpeed
      if (!speed || speed <= 0) return null
      const lastReal = this._rowsUpTo()
        .filter(r => r.ec_actual_progress !== null && r.ec_actual_progress !== '')
        .slice(-1)[0]
      if (!lastReal) return null
      const remaining = 100 - parseFloat(lastReal.ec_actual_progress)
      if (remaining <= 0) return null
      const weeksLeft = remaining / speed
      const lastDate  = new Date(lastReal.date + 'T00:00:00')
      lastDate.setDate(lastDate.getDate() + Math.round(weeksLeft * 7))
      return lastDate.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' })
    },

    projectedDaysOver() {
      const speed = this.advanceSpeed
      if (!speed || speed <= 0) return null
      const lastReal = this._rowsUpTo()
        .filter(r => r.ec_actual_progress !== null && r.ec_actual_progress !== '')
        .slice(-1)[0]
      if (!lastReal) return null
      const remaining = 100 - parseFloat(lastReal.ec_actual_progress)
      if (remaining <= 0) return null
      const weeksLeft  = remaining / speed
      const lastDate   = new Date(lastReal.date + 'T00:00:00')
      const projected  = new Date(lastDate)
      projected.setDate(projected.getDate() + Math.round(weeksLeft * 7))
      const contractEnd = this.contractEndDate
      if (!contractEnd) return null
      const diff = Math.round((projected - contractEnd) / (1000 * 60 * 60 * 24))
      return diff
    },

    requiredSpeed() {
      // % per week needed from last real data point to finish on time
      const lastReal = this._rowsUpTo()
        .filter(r => r.ec_actual_progress !== null && r.ec_actual_progress !== '')
        .slice(-1)[0]
      if (!lastReal) return null
      const contractEnd = this.contractEndDate
      if (!contractEnd) return null
      const remaining  = 100 - parseFloat(lastReal.ec_actual_progress)
      if (remaining <= 0) return null
      const lastDate   = new Date(lastReal.date + 'T00:00:00')
      const msLeft     = contractEnd - lastDate
      const weeksLeft  = msLeft / (1000 * 60 * 60 * 24 * 7)
      if (weeksLeft <= 0) return null
      return parseFloat((remaining / weeksLeft).toFixed(2))
    },

    // ── Existing computed ────────────────────────────────────────────────

    canGenerate() {
      const { startDate, endDate, cutoffDay } = this.config
      if (!startDate || !endDate || cutoffDay === null) return false
      return new Date(startDate + 'T00:00:00') <= new Date(endDate + 'T00:00:00')
    },

    visibleColspan() { return (this.showEc ? 1 : 0) + (this.showPh ? 1 : 0) },
    lastEcPlan()   { return this._lastVal('ec_programmed_progress') },
    lastPhPlan()   { return this._lastVal('ph_programmed_progress') },
    lastActualEc() { return this._lastVal('ec_actual_progress') },
    lastActualPh() { return this._lastVal('ph_actual_progress') },
    lastReprogEc() { return this._lastReprogVal('ec_progress') },
    lastReprogPh() { return this._lastReprogVal('ph_progress') },

    // Effective plan: máximo entre plan original y todas las reprogramaciones (por componente EC/PH)
    lastEcPlanEffective() {
      // start with lastEcPlan (may be null)
      const base = this.lastEcPlan !== null ? parseFloat(this.lastEcPlan) : null
      if (!this.reprogramLabels.length) return base !== null ? base.toFixed(1) : null

      // collect last value of each reprogram index across rows (último no-nulo por index)
      const reprogMaxes = []
      for (let i = 0; i < this.reprogramLabels.length; i++) {
        const vals = this._rowsUpTo()
          .filter(r => r.reprogram && r.reprogram[i] && r.reprogram[i].ec_progress !== null && r.reprogram[i].ec_progress !== undefined && r.reprogram[i].ec_progress !== '')
          .map(r => r.reprogram[i].ec_progress)
        if (vals.length) reprogMaxes.push(parseFloat(vals[vals.length - 1]))
      }

      const candidates = reprogMaxes.slice()
      if (base !== null) candidates.push(base)
      if (!candidates.length) return null
      return Math.max(...candidates).toFixed(1)
    },

    lastPhPlanEffective() {
      const base = this.lastPhPlan !== null ? parseFloat(this.lastPhPlan) : null
      if (!this.reprogramLabels.length) return base !== null ? base.toFixed(1) : null

      const reprogMaxes = []
      for (let i = 0; i < this.reprogramLabels.length; i++) {
        const vals = this._rowsUpTo()
          .filter(r => r.reprogram && r.reprogram[i] && r.reprogram[i].ph_progress !== null && r.reprogram[i].ph_progress !== undefined && r.reprogram[i].ph_progress !== '')
          .map(r => r.reprogram[i].ph_progress)
        if (vals.length) reprogMaxes.push(parseFloat(vals[vals.length - 1]))
      }

      const candidates = reprogMaxes.slice()
      if (base !== null) candidates.push(base)
      if (!candidates.length) return null
      return Math.max(...candidates).toFixed(1)
    },

    // Adelanto / Retraso: real - planificación_effective
    adelantoRetrasoEc() {
      const real = this.lastActualEc !== null ? parseFloat(this.lastActualEc) : null
      const plan = this.lastEcPlanEffective !== null ? parseFloat(this.lastEcPlanEffective) : null
      if (real === null || plan === null) return null
      return (real - plan).toFixed(1)
    },

    adelantoRetrasoPh() {
      const real = this.lastActualPh !== null ? parseFloat(this.lastActualPh) : null
      const plan = this.lastPhPlanEffective !== null ? parseFloat(this.lastPhPlanEffective) : null
      if (real === null || plan === null) return null
      return (real - plan).toFixed(1)
    },

    desvEc() {
      const real = this.lastActualEc !== null ? parseFloat(this.lastActualEc) : null
      if (real === null) return null
      const ref = this.reprogramLabels.length && this.lastReprogEc !== null
        ? parseFloat(this.lastReprogEc)
        : (this.lastEcPlan !== null ? parseFloat(this.lastEcPlan) : null)
      return ref !== null ? (real - ref).toFixed(1) : null
    },

    desvPh() {
      const real = this.lastActualPh !== null ? parseFloat(this.lastActualPh) : null
      if (real === null) return null
      const ref = this.reprogramLabels.length && this.lastReprogPh !== null
        ? parseFloat(this.lastReprogPh)
        : (this.lastPhPlan !== null ? parseFloat(this.lastPhPlan) : null)
      return ref !== null ? (real - ref).toFixed(1) : null
    },

    desvBg() {
      // Use EC adelanto/retraso for bg decision; fallback neutral
      if (this.adelantoRetrasoEc === null) return '#F9FAFB'
      return parseFloat(this.adelantoRetrasoEc) >= 0 ? '#F0FDF4' : '#FFF1F2'
    },

    desvIconColor() {
      if (this.adelantoRetrasoEc === null) return '#6B7280'
      return parseFloat(this.adelantoRetrasoEc) >= 0 ? '#16A34A' : '#DC2626'
    },

    chartOverlay() {
      const rows = this._rowsUpTo()
      if (!rows || !rows.length) return null

      let lastIdx = -1
      for (let i = rows.length - 1; i >= 0; i--) {
        const r = rows[i]
        if ((r.ec_actual_progress !== null && r.ec_actual_progress !== '' && r.ec_actual_progress !== undefined) ||
            (r.ph_actual_progress !== null && r.ph_actual_progress !== '' && r.ph_actual_progress !== undefined)) {
          lastIdx = i
          break
        }
      }
      if (lastIdx === -1) return null

      const lastRow = rows[lastIdx]

      const ecPlanned = this.lastEcPlanEffective !== null ? parseFloat(this.lastEcPlanEffective) : (this.lastEcPlan !== null ? parseFloat(this.lastEcPlan) : null)
      const ecReal = this.lastActualEc !== null ? parseFloat(this.lastActualEc) : null
      const ecDelta = this.adelantoRetrasoEc !== null ? parseFloat(this.adelantoRetrasoEc) : null

      const phPlanned = this.lastPhPlanEffective !== null ? parseFloat(this.lastPhPlanEffective) : (this.lastPhPlan !== null ? parseFloat(this.lastPhPlan) : null)
      const phReal = this.lastActualPh !== null ? parseFloat(this.lastActualPh) : null
      const phDelta = this.adelantoRetrasoPh !== null ? parseFloat(this.adelantoRetrasoPh) : null

      return {
        date: lastRow.date || null,
        ec: { planned: ecPlanned, real: ecReal, delta: ecDelta },
        ph: { planned: phPlanned, real: phReal, delta: phDelta },
      }
    },
  },

  mounted() {
    if (this.selectedProject) {
      this.getProjectPlanning()
    }
  },

  methods: {
    hasPermission(permission) {
      try { return checkPermissions([permission]) }
      catch (e) { return false }
    },
    // ── API load ──────────────────────────────────────────────────────────
    async getProjectPlanning() {
      this.loading = true
      try {
        const data = await getGeneralProjectPlanning({ project_id: this.selectedProject.id })
        if (data && data.progresses && data.progresses.length) {
          const progresses = data.progresses
          const toDate = (d) => d ? d.substring(0, 10) : ''

          const maxReprogs = Math.max(...progresses.map(p => (p.reprograms || []).length))
          this.reprogramLabels = Array.from({ length: maxReprogs }, (_, i) => `Reprog. ${i + 1}`)

          this.rows = progresses.map(p => ({
            _id: Math.random(),
            id: p.id,
            date: toDate(p.date),
            ec_programmed_progress: p.ec_programmed_progress,
            ph_programmed_progress: p.ph_programmed_progress,
            ec_actual_progress: p.ec_actual_progress,
            ph_actual_progress: p.ph_actual_progress,
            reprogram: Array.from({ length: maxReprogs }, (_, idx) => {
              const rep = p.reprograms && p.reprograms[idx]
              return {
                id: rep ? rep.id : null,
                ec_progress: rep ? rep.ec_progress : null,
                ph_progress: rep ? rep.ph_progress : null,
              }
            }),
          }))
          // ensure rows are ordered by date (ascending) and normalize config dates
          this.rows.sort((a, b) => a.date.localeCompare(b.date))
          if (this.rows.length) {
            this.config.startDate = this.rows[0].date
            this.config.endDate   = this.rows[this.rows.length - 1].date
          }
          this.configLocked = true
        }
      } finally {
        this.loading = false
      }
    },

    // ── Config ────────────────────────────────────────────────────────────
    generatePeriods() {
      if (!this.canGenerate) return
      if (!this.hasPermission('create_planning')) {
        this.$store.globalStore.dispatch('addMessage', { message: 'No tienes permisos para generar planificación.', title: 'Permisos', type: 'warning', created: new Date() })
        return
      }
      const { startDate, endDate, cutoffDay } = this.config
      const end = new Date(endDate + 'T00:00:00')
      const dayNum = parseInt(cutoffDay)
      const d = new Date(startDate + 'T00:00:00')
      const periods = []
      while (d.getDay() !== dayNum) {
        d.setDate(d.getDate() + 1)
        if (d > end) break
      }
      while (d <= end) {
        periods.push(d.toISOString().split('T')[0])
        d.setDate(d.getDate() + 7)
      }
      this.reprogramLabels = []
      this.rows = periods.map(date => this.makeRow(date))
      this.configLocked = true
      this.debouncedSave()
    },

    unlockConfig() { this.configLocked = false },

    // ── Rows ──────────────────────────────────────────────────────────────
    makeRow(date, id = null) {
      return {
        _id: Math.random(),
        id,
        date,
        ec_programmed_progress: null,
        ph_programmed_progress: null,
        ec_actual_progress: null,
        ph_actual_progress: null,
        reprogram: Array.from({ length: this.reprogramLabels.length }, () => this.makeReprog()),
      }
    },

    makeReprog(id = null) {
      return { id, ec_progress: null, ph_progress: null }
    },

    addRow() {
      let date
      if (!this.rows.length) {
        date = new Date().toISOString().split('T')[0]
      } else {
        const d = new Date(this.rows[this.rows.length - 1].date + 'T00:00:00')
        d.setDate(d.getDate() + 7)
        date = d.toISOString().split('T')[0]
      }
      if (!this.hasPermission('create_planning')) return
      this.rows.push(this.makeRow(date))
    },

    removeRow(index) {
      if (!this.hasPermission('create_planning')) return
      if (this.rows.length > 1) this.rows.splice(index, 1)
    },

    // ── Reprogramaciones ──────────────────────────────────────────────────
    addReprogramLabel() {
      if (!this.hasPermission('create_planning')) return
      this.reprogramLabels.push(`Reprog. ${this.reprogramLabels.length + 1}`)
      this.rows.forEach(r => { r.reprogram = [...r.reprogram, this.makeReprog()] })
    },

    removeReprogramLabel(idx) {
      this.reprogramLabels.splice(idx, 1)
      this.rows.forEach(r => { r.reprogram = r.reprogram.filter((_, i) => i !== idx) })
    },

    updateReprog(row, idx, field, event) {
      if (!this.hasPermission('create_planning')) return
      const raw = event.target.value
      const val = raw === '' ? null : parseFloat(raw)
      const current = row.reprogram[idx] || this.makeReprog()
      row.reprogram.splice(idx, 1, { ...current, [field]: isNaN(val) ? null : val })
    },

    // ── Excel import ──────────────────────────────────────────────────────
    triggerFileInput() {
      if (!this.hasPermission('create_planning')) {
        this.importError = 'No tienes permisos para importar planificación.'
        return
      }
      this.importError = ''
      this.$refs.excelFileInput.value = ''
      this.$refs.excelFileInput.click()
    },

    onExcelFileSelected(event) {
      if (!this.hasPermission('create_planning')) {
        this.importError = 'No tienes permisos para importar planificación.'
        return
      }
      const file = event.target.files[0]
      if (!file) return
      this.importingExcel = true
      this.importError = ''

      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, { type: 'array', cellDates: true })
          const sheetName = workbook.SheetNames[0]
          const sheet = workbook.Sheets[sheetName]
          const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: null, raw: false })

          const dataRows = []
          for (const rowArr of raw) {
            if (!rowArr || rowArr.length < 2) continue
            const dateVal = rowArr[0]
            if (!dateVal) continue
            const parsed = this.parseExcelDate(dateVal)
            if (!parsed) continue

            const ec = this.parsePercentage(rowArr[1])
            const ph = this.parsePercentage(rowArr[2])
            dataRows.push({ date: parsed, ec, ph })
          }

          if (!dataRows.length) {
            this.importError = 'No se encontraron filas válidas. Verifica que la primera columna tenga fechas y las siguientes tengan valores porcentuales.'
            this.importingExcel = false
            return
          }

          dataRows.sort((a, b) => a.date.localeCompare(b.date))

          if (this.rows.length) {
            const existingByDate = {}
            this.rows.forEach(r => { existingByDate[r.date] = r })

            const merged = dataRows.map(({ date, ec, ph }) => {
              if (existingByDate[date]) {
                const existing = existingByDate[date]
                existing.ec_programmed_progress = ec
                existing.ph_programmed_progress = ph
                return existing
              }
              return this.makeRow(date)
            })

            const importedDates = new Set(dataRows.map(r => r.date))
            this.rows.forEach(r => {
              if (!importedDates.has(r.date)) merged.push(r)
            })
            merged.sort((a, b) => a.date.localeCompare(b.date))
            this.rows = merged
          } else {
            this.rows = dataRows.map(({ date, ec, ph }) => {
              const row = this.makeRow(date)
              row.ec_programmed_progress = ec
              row.ph_programmed_progress = ph
              return row
            })
          }

          if (this.rows.length) {
            this.config.startDate = this.rows[0].date
            this.config.endDate   = this.rows[this.rows.length - 1].date
            this.configLocked = true
          }

          this.debouncedSave()
        } catch (err) {
          console.error('Excel import error:', err)
          this.importError = 'Error al leer el archivo. Asegúrate de que sea un Excel válido (.xlsx o .xls).'
        } finally {
          this.importingExcel = false
        }
      }

      reader.onerror = () => {
        this.importError = 'No se pudo leer el archivo.'
        this.importingExcel = false
      }

      reader.readAsArrayBuffer(file)
    },

    parseExcelDate(val) {
      if (!val && val !== 0) return null
      if (val instanceof Date) {
        if (isNaN(val.getTime())) return null
        return val.toISOString().split('T')[0]
      }
      if (typeof val === 'number') {
        const d = XLSX.SSF.parse_date_code(val)
        if (!d) return null
        const mm = String(d.m).padStart(2, '0')
        const dd = String(d.d).padStart(2, '0')
        return `${d.y}-${mm}-${dd}`
      }
      const s = String(val).trim()
      const dmy = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/)
      if (dmy) {
        const iso = `${dmy[3]}-${dmy[2].padStart(2,'0')}-${dmy[1].padStart(2,'0')}`
        if (!isNaN(new Date(iso).getTime())) return iso
      }
      const ymd = s.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/)
      if (ymd) {
        const iso = `${ymd[1]}-${ymd[2].padStart(2,'0')}-${ymd[3].padStart(2,'0')}`
        if (!isNaN(new Date(iso).getTime())) return iso
      }
      const d = new Date(s)
      if (!isNaN(d.getTime())) return d.toISOString().split('T')[0]
      return null
    },

    parsePercentage(val) {
      if (val === null || val === undefined || val === '') return null
      if (typeof val === 'number') {
        let n = val
        if (n > 0 && n <= 1) n = n * 100
        return Math.min(100, Math.max(0, parseFloat(n.toFixed(2))))
      }
      let s = String(val).trim().replace('%', '').replace(/\s/g, '')
      const hasDot = s.indexOf('.') !== -1
      const hasComma = s.indexOf(',') !== -1
      if (hasDot && hasComma) {
        if (s.indexOf('.') < s.indexOf(',')) {
          s = s.replace(/\./g, '')
          s = s.replace(',', '.')
        } else {
          s = s.replace(/,/g, '')
        }
      } else if (hasComma && !hasDot) {
        const parts = s.split(',')
        if (parts.length === 2 && parts[1].length <= 2) {
          s = parts[0] + '.' + parts[1]
        } else {
          s = parts.join('')
        }
      } else if (hasDot && !hasComma) {
        const parts = s.split('.')
        if (parts.length > 2) {
          const last = parts.pop()
          s = parts.join('') + '.' + last
        }
      }
      const n = parseFloat(s)
      if (isNaN(n)) return null
      let num = n
      if (num > 0 && num <= 1) num = num * 100
      return Math.min(100, Math.max(0, parseFloat(num.toFixed(2))))
    },

    // ── Drag resize ───────────────────────────────────────────────────────
    startDrag(e) {
      this.dragging = true
      this.dragStartX = e.clientX
      this.dragStartPct = this.tablePct

      const onMove = (ev) => {
        if (!this.dragging) return
        const container = this.$refs.splitContainer
        if (!container) return
        const totalW = container.offsetWidth
        const delta = ((ev.clientX - this.dragStartX) / totalW) * 100
        this.tablePct = parseFloat(Math.min(85, Math.max(15, this.dragStartPct + delta)).toFixed(2))
      }

      const onUp = () => {
        this.dragging = false
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }

      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },

    // ── Helpers ───────────────────────────────────────────────────────────
    todayISO() {
      const d = new Date()
      return d.toISOString().split('T')[0]
    },

    _rowsUpTo(date) {
      const limit = date || this.todayISO()
      return this.rows
        .filter(r => r && r.date && r.date <= limit)
        .slice()
        .sort((a, b) => a.date.localeCompare(b.date))
    },

    _lastVal(field) {
      const vals = this._rowsUpTo().filter(r => r[field] !== null && r[field] !== undefined && r[field] !== '').map(r => r[field])
      return vals.length ? parseFloat(vals[vals.length - 1]).toFixed(1) : null
    },

    _lastReprogVal(field) {
      if (!this.reprogramLabels.length) return null
      const li = this.reprogramLabels.length - 1
      const vals = this._rowsUpTo().filter(r => {
        const rep = r.reprogram && r.reprogram[li]
        return rep && rep[field] !== null && rep[field] !== undefined && rep[field] !== ''
      }).map(r => r.reprogram[li][field])
      return vals.length ? parseFloat(vals[vals.length - 1]).toFixed(1) : null
    },

    isDateAfterToday(date) {
      if (!date) return false
      const today = new Date()
      today.setHours(0,0,0,0)
      const d = new Date(date + 'T00:00:00')
      d.setHours(0,0,0,0)
      return d > today
    },

    tabIdx(rowIndex, colIndex) {
      return colIndex * this.rows.length + rowIndex + 1
    },

    formatCurrency(value) {
      if (value === null || value === undefined) return ''
      if (this.selectedProject?.details?.currency === 1) {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        }).format(Math.round(value)) + ' USD'
      } else if (this.selectedProject?.details?.currency === 2) {
        return new Intl.NumberFormat('es-CL', {
          style: 'currency',
          currency: 'CLP',
          maximumFractionDigits: 0,
        }).format(Math.round(value)) + ' CLP'
      } else {
        return this.$options.filters.toThousandFilter(Math.round(value), 2) + ' ' + (this.selectedProject?.details?.currency_info?.code || '')
      }
    },

    // ── API save ──────────────────────────────────────────────────────────
    async saveTableData() {
      // If user cannot create nor update progress, nothing to save
      const canCreate = this.hasPermission('create_planning')
      const canUpdate = this.hasPermission('update_planning_progress')
      if (!canCreate && !canUpdate) return

      const payload = {
        project_id: this.selectedProject.id,
        data: this.rows.map(r => {
          const out = {}
          if (r.id) out.id = r.id
          if (canCreate) {
            out.date = r.date
            out.ec_programmed_progress = r.ec_programmed_progress ?? null
            out.ph_programmed_progress = r.ph_programmed_progress ?? null
            out.reprogram = (r.reprogram || [])
              .filter(rep => rep.ec_progress !== null || rep.ph_progress !== null)
              .map(rep => ({ ...(rep.id ? { id: rep.id } : {}), ec_progress: rep.ec_progress ?? null, ph_progress: rep.ph_progress ?? null }))
          }
          if (canUpdate) {
            out.ec_actual_progress = r.ec_actual_progress ?? null
            out.ph_actual_progress = r.ph_actual_progress ?? null
          }
          return out
        }),
      }

      await setGeneralProjectPlanning(payload)
    },
  },
}
</script>

<style scoped>
/* ── Container ───────────────────────────────────────────────────────── */
.planning-container {
  font-family: 'Inter', 'DM Sans', 'Segoe UI', sans-serif;
  background: #fff;
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

/* ── Config bar ──────────────────────────────────────────────────────── */
.config-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px 16px;
  background: #F9FAFB;
  border-bottom: 1px solid #E5E7EB;
  flex-shrink: 0;
}
.config-label {
  font-size: 11px;
  font-weight: 600;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}
.config-field    { max-width: 148px; min-width: 130px; }
.config-field-sm { max-width: 130px; min-width: 110px; }
::v-deep .config-field .v-input__control,
::v-deep .config-field-sm .v-input__control {
  border: 1px solid #E5E7EB !important;
  border-radius: 6px !important;
  background: #fff !important;
}
::v-deep .config-field .v-input__slot,
::v-deep .config-field-sm .v-input__slot {
  min-height: 30px !important;
  padding: 0 8px !important;
  font-size: 12px !important;
}
.generate-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
  font-size: 12px !important;
}

/* ── Resizable split ─────────────────────────────────────────────────── */
.content-split {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;
  user-select: none;
}

.table-pane,
.chart-pane {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
}

.pane-inner {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  padding: 10px 14px;
}

/* ── Drag handle ─────────────────────────────────────────────────────── */
.drag-handle {
  width: 10px;
  flex-shrink: 0;
  background: #F3F4F6;
  border-left: 1px solid #E5E7EB;
  border-right: 1px solid #E5E7EB;
  cursor: col-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, border-color 0.15s;
  z-index: 3;
}
.drag-handle:hover,
.drag-handle--active {
  background: #EEF2FF;
  border-color: #A5B4FC;
}
.drag-grip {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.drag-grip span {
  display: block;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #D1D5DB;
  transition: background 0.15s;
}
.drag-handle:hover .drag-grip span,
.drag-handle--active .drag-grip span { background: #6366F1; }

/* ── KPI Accordion ───────────────────────────────────────────────────── */
.kpi-accordion {
  flex-shrink: 0;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  margin-bottom: 10px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}

/* Collapsed bar */
.kpi-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px 7px 12px;
  cursor: pointer;
  background: #F8FAFF;
  border-bottom: 2px solid transparent;
  border-left: 3px solid #6366F1;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
  user-select: none;
  flex-wrap: nowrap;
  overflow: hidden;
}
.kpi-accordion--open .kpi-bar {
  border-bottom-color: #E5E7EB;
  background: #F0F1FF;
}
.kpi-bar:hover {
  background: #EDEEFF;
  box-shadow: inset 0 -2px 0 #6366F1;
}

/* Scrollable KPI items — takes all available space, hides overflow */
.kpi-bar-items {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.kpi-bar-sep {
  width: 1px;
  height: 14px;
  background: #D1D5DB;
  flex-shrink: 0;
}
.kpi-bar-item {
  display: flex;
  align-items: center;
  gap: 4px;
}
.kpi-bar-label {
  font-size: 10px;
  font-weight: 600;
  color: #9CA3AF;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}
.kpi-bar-val {
  font-size: 12px;
  font-weight: 700;
  color: #111827;
  white-space: nowrap;
}

/* Toggle button */
.kpi-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px 3px 10px;
  border-radius: 20px;
  border: 1px solid #E0E0FF;
  background: #fff;
  font-size: 11px;
  font-weight: 600;
  color: #6366F1;
  white-space: nowrap;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
  flex-shrink: 0;
  cursor: pointer;
}
.kpi-bar:hover .kpi-toggle-btn {
  background: #EEF2FF;
  border-color: #6366F1;
  box-shadow: 0 0 0 2px #EEF2FF;
}
.kpi-toggle-label {
  font-size: 10px;
  font-weight: 700;
  color: #6366F1;
  letter-spacing: 0.02em;
}

/* Status badge */
.kpi-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 700;
  border-radius: 20px;
  padding: 2px 8px;
  letter-spacing: 0.02em;
  white-space: nowrap;
}
.kpi-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
}

.status--ok      { background: #F0FDF4; color: #16A34A; }
.status--ok      .kpi-status-dot { background: #16A34A; }
.status--warn    { background: #FFFBEB; color: #D97706; }
.status--warn    .kpi-status-dot { background: #D97706; }
.status--danger  { background: #FFF1F2; color: #DC2626; }
.status--danger  .kpi-status-dot { background: #DC2626; }
.status--neutral { background: #F3F4F6; color: #6B7280; }
.status--neutral .kpi-status-dot { background: #9CA3AF; }

/* Expanded grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: #E5E7EB;
}

.kpi-card {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  background: #fff;
  transition: background 0.12s;
}
.kpi-card:hover { background: #FAFAFA; }

.kpi-card-icon {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
}

.kpi-card-body { display: flex; flex-direction: column; gap: 1px; min-width: 0; }

.kpi-card-label {
  font-size: 9px;
  font-weight: 700;
  color: #9CA3AF;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  white-space: normal;
  word-break: break-word;
  line-height: 1.3;
}
.kpi-card-value {
  font-size: 13px;
  font-weight: 800;
  color: #111827;
  white-space: normal;
  word-break: break-word;
  line-height: 1.2;
}
.kpi-card-sub {
  font-size: 10px;
  font-weight: 500;
  color: #6B7280;
  white-space: normal;
  word-break: break-word;
  line-height: 1.3;
}

/* ── KPI tooltip content (inside v-menu) ─────────────────────────────── */
.kpi-tooltip {
  padding: 10px 12px;
  max-width: 260px;
  background: #fff;
}
.kpi-tooltip-title {
  font-size: 12.5px;
  font-weight: 700;
  color: #1F2937;
  margin-bottom: 5px;
  line-height: 1.3;
}
.kpi-tooltip-body {
  font-size: 11.5px;
  font-weight: 400;
  color: #4B5563;
  line-height: 1.5;
}

/* ── Table ───────────────────────────────────────────────────────────── */
.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  margin-bottom: 8px;
}
.section-title {
  font-size: 11px;
  font-weight: 700;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.table-wrapper {
  overflow: auto;
  flex: 1;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
}
.table-wrapper::-webkit-scrollbar { width: 4px; height: 4px; }
.table-wrapper::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 4px; }

table { width: 100%; border-collapse: collapse; font-size: 12px; }

thead tr { background: #F3F4F6; }
.col-group-row { position: sticky; top: 0; z-index: 3; }
.col-sub-row   { position: sticky; top: 29px; z-index: 3; background: #F3F4F6; }

th {
  padding: 5px 6px;
  text-align: center;
  font-weight: 700;
  color: #6B7280;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
  border-bottom: 2px solid #E5E7EB;
}
.th-left { text-align: left; }

.grp-header  { border-bottom-width: 2px; }
.grp-plan    { color: #2D4B95; border-bottom-color: #2D4B95; }
.grp-actual  { color: #16A34A; border-bottom-color: #16A34A; }

.reprog-grp-label {
  display: flex;
  align-items: center;
  justify-content: center;
}

.sub-th { font-size: 9px; letter-spacing: 0.08em; border-bottom: 1px solid #E5E7EB; }
.ec-th  { color: #2D4B95; }
.ph-th  { color: #0EA5E9; }
.sub-th-ref { font-size: 8px; color: #9CA3AF; font-weight: 400; text-transform: none; letter-spacing: 0; }

td { padding: 3px 5px; border-top: 1px solid #F3F4F6; vertical-align: top; }
tbody tr:hover { background: #F9FAFB; }

/* ── Percent inputs ───────────────────────────────────────────────────── */
.pct-wrap {
  display: flex;
  align-items: center;
  gap: 2px;
  border: 1px solid #E5E7EB;
  border-radius: 5px;
  padding: 2px 4px;
  width: 50px;
  margin: 0 auto;
  transition: border-color 0.15s;
  background: #fff;
}
.ec-wrap:focus-within     { border-color: #2D4B95; }
.ph-wrap:focus-within     { border-color: #0EA5E9; }
.actual-wrap:focus-within { border-color: #16A34A; }

.pct-input {
  border: none;
  outline: none;
  width: 26px;
  font-size: 11px;
  color: #111827;
  background: transparent;
  -moz-appearance: textfield;
  appearance: textfield;
}
.is-disabled {
  opacity: 0.45;
  pointer-events: none;
}
.pct-input::-webkit-outer-spin-button,
.pct-input::-webkit-inner-spin-button { -webkit-appearance: none; }
.pct-sym { font-size: 10px; color: #9CA3AF; }

.ref-value {
  font-size: 9px;
  color: #2D4B95;
  text-align: center;
  margin-top: 2px;
  white-space: nowrap;
}
.ref-value--actual { color: #16A34A; }

/* ── Date input ──────────────────────────────────────────────────────── */
.date-wrap {
  display: flex;
  align-items: center;
  border: 1px solid #E5E7EB;
  border-radius: 5px;
  padding: 2px 3px;
  width: 98px;
  margin: 0 auto;
  transition: border-color 0.15s;
  background: #fff;
}
.date-wrap:focus-within { border-color: #6B7280; }
.date-input {
  border: none;
  outline: none;
  font-size: 10px;
  font-weight: 600;
  color: #374151;
  background: transparent;
  width: 90px;
  cursor: pointer;
}
.date-input::-webkit-calendar-picker-indicator { width: 10px; height: 10px; padding: 0; opacity: 0.5; cursor: pointer; }

/* ── Desvío colors ───────────────────────────────────────────────────── */
.desv-pos  { color: #16A34A; }
.desv-neg  { color: #DC2626; }
.desv-warn { color: #D97706; }

/* ── Notice bar ─────────────────────────────────────────────────────── */
.widget-notice {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 14px;
  background: #FFFBEB;
  border-bottom: 1px solid #FDE68A;
  flex-shrink: 0;
  min-height: 26px;
}

.widget-notice--active {
  background: #F0FDF4;
  border-bottom-color: #BBF7D0;
}

.notice-left {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  color: #92400E;
  flex-wrap: wrap;
  line-height: 1.3;
}

.widget-notice--active .notice-left {
  color: #166534;
}

.notice-btn {
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border-radius: 20px;
  border: 1px solid #D97706;
  background: #fff;
  font-size: 10px;
  font-weight: 600;
  color: #D97706;
  cursor: pointer;
  outline: none;
  transition: background 0.15s, color 0.15s;
  margin-left: 4px;
  white-space: nowrap;
}
.notice-btn:hover {
  background: #FEF3C7;
}

.notice-btn--green {
  border-color: #16A34A;
  color: #16A34A;
}
.notice-btn--green:hover {
  background: #DCFCE7;
}


.entrega-approval-menu {
  border: 2px solid #2D4B95 !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 14px rgba(45, 75, 149, 0.18) !important;
}

/* ── Empty state ─────────────────────────────────────────────────────── */
.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width: 900px) {
  .content-split { flex-direction: column; }
  .table-pane,
  .chart-pane    { width: 100% !important; }
  .drag-handle {
    width: 100%; height: 10px;
    cursor: row-resize;
    border-left: none; border-right: none;
    border-top: 1px solid #E5E7EB; border-bottom: 1px solid #E5E7EB;
  }
  .drag-grip       { flex-direction: row; }
  .table-pane      { max-height: 50%; }
  .kpi-grid        { grid-template-columns: repeat(2, 1fr); }
}
</style>